export type LocalizedContentDraft = {
  name: string;
  slug: string;
  shortDescription: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
};

export type CategoryTranslationDraft = Pick<
  LocalizedContentDraft,
  "name" | "slug" | "description" | "seoDescription"
>;

export const EMPTY_LOCALIZED_CONTENT: LocalizedContentDraft = {
  name: "",
  slug: "",
  shortDescription: "",
  description: "",
  seoTitle: "",
  seoDescription: "",
};

export const EMPTY_CATEGORY_TRANSLATION: CategoryTranslationDraft = {
  name: "",
  slug: "",
  description: "",
  seoDescription: "",
};

export function normalizeCmsLocales(
  value: unknown,
  currentLocale: string,
): { locales: string[]; defaultLocale: string } {
  const settings = value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>).settings
    : undefined;
  const record = settings && typeof settings === "object" && !Array.isArray(settings)
    ? settings as Record<string, unknown>
    : {};
  const configured = Array.isArray(record.locales)
    ? record.locales.filter((locale): locale is string => typeof locale === "string" && locale.trim().length > 0)
    : [];
  const locales = [...new Set(configured.map((locale) => locale.trim()))];
  if (!locales.length) locales.push(currentLocale || "nl");
  const configuredDefault = typeof record.defaultLocale === "string" ? record.defaultLocale.trim() : "";
  return {
    locales,
    defaultLocale: locales.includes(configuredDefault) ? configuredDefault : locales[0],
  };
}

export function exactTranslation<T extends { locale: string }>(
  translations: readonly T[],
  locale: string,
): T | undefined {
  return translations.find((translation) => translation.locale === locale);
}

export function fallbackTranslationLocale<T extends { locale: string }>(
  translations: readonly T[],
  locale: string,
  defaultLocale: string,
): string | undefined {
  if (exactTranslation(translations, locale)) return undefined;
  return exactTranslation(translations, defaultLocale)?.locale ?? translations[0]?.locale;
}

export function serializableTranslations<T extends CategoryTranslationDraft>(
  translations: Readonly<Record<string, T>>,
  locales: readonly string[],
): Array<T & { locale: string }> {
  return locales.flatMap((locale) => {
    const translation = translations[locale];
    if (!translation?.name.trim()) return [];
    return [{
      ...translation,
      locale,
      name: translation.name.trim(),
      slug: translation.slug.trim(),
      description: translation.description.trim(),
      ...("shortDescription" in translation
        ? { shortDescription: String(translation.shortDescription).trim() }
        : {}),
      ...("seoTitle" in translation ? { seoTitle: String(translation.seoTitle).trim() } : {}),
      ...("seoDescription" in translation
        ? { seoDescription: String(translation.seoDescription).trim() }
        : {}),
    }];
  });
}

export function localizedBlockText(value: unknown, locale: string): string {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object" || Array.isArray(value)) return "";
  const translations = value as Record<string, unknown>;
  const exact = translations[locale];
  if (typeof exact === "string") return exact;
  const base = translations[locale.split(/[-_]/u)[0]];
  if (typeof base === "string") return base;
  return Object.values(translations).find((candidate): candidate is string => typeof candidate === "string") ?? "";
}

/** Presentation-only expansion; the category identity and URL stay canonical. */
export function localizedCategoryCta(value: unknown, locale: string, categoryName: string): string {
  return localizedBlockText(value, locale)
    .replaceAll("{name}", categoryName)
    .replaceAll("{category}", categoryName);
}

/** Word-based card excerpt; leaves the saved category description untouched. */
export function categoryCardExcerpt(description: string, wordLimit: number): string {
  if (!Number.isSafeInteger(wordLimit) || wordLimit < 1 || wordLimit > 100) return description;
  const words = description.trim().split(/\s+/u);
  return words.length > wordLimit ? `${words.slice(0, wordLimit).join(" ")}…` : description;
}

export function updateLocalizedBlockText(
  value: unknown,
  locale: string,
  next: string,
): Record<string, string> {
  const previous = value && typeof value === "object" && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === "string"))
    : typeof value === "string" && value
      ? { [locale]: value }
      : {};
  return { ...previous, [locale]: next };
}
